import { styled, theme } from '@/src/stitches.config'
import React, { useEffect } from 'react'
import { PGPlusStep2RightContent } from '@/src/modules/pg-plus/content/PGPlusStep2RightContent'
import { PGPlusLeftContent } from '@/src/modules/pg-plus/content/PGPlusLeftContent'
import {
    SESSION_STORAGE_BRAND_OBJECT_ID,
    SESSION_STORAGE_BRAND_QUERY_ID,
} from '@/src/modules/algolia/constants'
import { PGPlusBACVisited } from '@/src/common/utilities/gtag'
import { useGiftQuantityContext } from '@/src/common/contexts/GiftQuantityContext'
import {
    FORM_FIELDS_WRAPPER_ID,
    SET_ALGOLIA_STATE,
    SET_BRAND_STATE,
} from '@/src/modules/build-a-card/constants'
import useWindowSize from '@/src/common/hooks/useWindowSize'
import BrandDescriptionWithOptionalCTA from '@/src/modules/pg-plus/content/BrandDescriptionWithOptionalCTA'
import {
    BACContainerLeft,
    BACContainerRight,
    ComponentContent,
} from '@/src/modules/build-a-card/StyledComponents'
import { useBasicGiftCardContext } from '@/src/common/contexts/BasicGiftCardContext'
import {
    TMinMaxFromGYPMerchant,
    TPrismaBrandData,
} from '@/src/modules/build-a-card/types'
import { PG_PLUS_NAME } from '@/prisma/schemaConstants'
import determineValidRedemptionOptions from '@/src/modules/partners/utilities/determineValidRedemptionOptions'
import { StyledForm } from '@/src/modules/pg-plus/PGPlusStyledComponents'
import PGPlusFormStep1 from '@/src/modules/pg-plus/forms/PGPlusFormStep1'
import { usePartnerTheme } from '@/src/common/contexts/PartnerThemeContext'
import sanitizeBrandHtmlContent from '../brand-cms/sanitizeBrandHtmlContent'

const PGPlusContent = styled('div', {
    display: 'flex',
    width: '1440px',
    maxWidth: '100%',
    margin: '0 auto',
    flexDirection: 'column',
    '@md': {
        flexDirection: 'row',
    },
    '& h1': {
        '@lg': {
            maxWidth: '600px',
        },
    },
    '.gradient': {
        background: `-webkit-linear-gradient(83.53deg, ${theme.colors.primary} 18.47%, ${theme.colors.secondary} 78%)`,
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    '@sm': {
        maxWidth: '100%',
    },
})

const StyledContainerLeft = styled(BACContainerLeft, {
    '.card-image': {
        mt: 30,
    },
})

interface IPGFlow {
    builderBrand: {
        description: any
        h1Text: any
        showCustomH1: boolean
    }
    brandData: TPrismaBrandData
    trendingItems: [] | null
    minMaxFixedDenoms: TMinMaxFromGYPMerchant
}

const PGPlusFlow = ({
    builderBrand,
    brandData,
    trendingItems,
    minMaxFixedDenoms,
}: IPGFlow) => {
    const {
        designName,
        denomination,
        currentStep,
        setCurrentStep,
        partnerData,
    } = useBasicGiftCardContext()
    const { analyticWasSent, setAnalyticWasSent, dispatch, cardQuantity } =
        useGiftQuantityContext()
    const partnerTheme = usePartnerTheme()

    const {
        name: brandName,
        image: brandImage,
        brandSlug,
        brandUuid,
        isPgPlus,
        brandCategories = [],
        eGiftEnabled,
        cardLinkEnabled,
    } = brandData
    const { width: viewportWidth = 900 } = useWindowSize()
    const isMobileView = Boolean(viewportWidth < 900)

    // considers both stored PGC and whitelabel brand
    const { allowCardLink, allowChangeMerchant, allowEGift, allowPlastic } =
        determineValidRedemptionOptions(
            cardLinkEnabled,
            eGiftEnabled,
            partnerData?.programConfig
        )
    const partnerAboutContent = sanitizeBrandHtmlContent(
        partnerData?.programConfig?.about_content ?? ''
    )

    useEffect(() => {
        dispatch({
            type: SET_BRAND_STATE,
            payload: {
                brandName,
                brandSlug,
                brandUuid,
                brandImage,
                brandCategories,
                isPgPlus,
            },
        })
    }, [
        brandCategories,
        brandSlug,
        brandUuid,
        dispatch,
        brandName,
        brandImage,
        isPgPlus,
    ])

    useEffect(() => {
        try {
            dispatch({
                type: SET_ALGOLIA_STATE,
                payload: {
                    brandObjectId:
                        sessionStorage.getItem(
                            SESSION_STORAGE_BRAND_OBJECT_ID
                        ) ?? '',
                    brandQueryId:
                        sessionStorage.getItem(
                            SESSION_STORAGE_BRAND_QUERY_ID
                        ) ?? '',
                },
            })
        } catch {}
        window.onunload = () => {
            sessionStorage.removeItem(SESSION_STORAGE_BRAND_OBJECT_ID)
            sessionStorage.removeItem(SESSION_STORAGE_BRAND_QUERY_ID)
        }
    }, [dispatch])

    // push datalayer event
    useEffect(() => {
        if (designName && !analyticWasSent) {
            PGPlusBACVisited(
                ['eec.detail', 'view_item'],
                brandName,
                designName,
                !!sessionStorage?.getItem(SESSION_STORAGE_BRAND_QUERY_ID),
                brandCategories,
                denomination,
                cardQuantity
            )
            setAnalyticWasSent(true)
        }
    }, [
        analyticWasSent,
        brandCategories,
        brandName,
        cardQuantity,
        denomination,
        designName,
        setAnalyticWasSent,
    ])

    return (
        <ComponentContent className={`${partnerTheme}`}>
            <PGPlusContent data-testid={'pg-plus-content'}>
                <StyledContainerLeft>
                    <PGPlusLeftContent
                        h1Text={builderBrand?.h1Text}
                        showCustomH1={builderBrand?.showCustomH1}
                        minMaxAndFixed={minMaxFixedDenoms}
                        redemptionOptions={{
                            eGiftEnabled: allowEGift,
                            cardLinkEnabled: allowCardLink,
                            plasticEnabled: allowPlastic,
                            changeMerchantEnabled: allowChangeMerchant,
                        }}
                        description={
                            partnerAboutContent || builderBrand?.description
                        }
                        trendingItems={trendingItems}
                        brandData={brandData}
                    />
                </StyledContainerLeft>
                <BACContainerRight id={FORM_FIELDS_WRAPPER_ID}>
                    <StyledForm data-testid={'pg-plus-form'}>
                        {currentStep === 1 && (
                            <PGPlusFormStep1
                                setCurrentStep={setCurrentStep}
                                minMaxAndFixed={minMaxFixedDenoms}
                            />
                        )}
                        {currentStep === 2 && <PGPlusStep2RightContent />}
                    </StyledForm>
                </BACContainerRight>
                {/*if in mobile view, show Brand Details and CTA below form*/}
                {isMobileView && (
                    <BrandDescriptionWithOptionalCTA
                        adjustedBrandName={isPgPlus ? PG_PLUS_NAME : brandName}
                        description={
                            partnerAboutContent ?? builderBrand?.description
                        }
                        minMaxAndFixed={minMaxFixedDenoms}
                        redemptionOptions={{
                            eGiftEnabled: allowEGift,
                            cardLinkEnabled: allowCardLink,
                            plasticEnabled: allowPlastic,
                            changeMerchantEnabled: allowChangeMerchant,
                        }}
                        trendingItems={trendingItems}
                        partnerData={partnerData}
                        currentStep={currentStep}
                        startOpen={false}
                    />
                )}
            </PGPlusContent>
        </ComponentContent>
    )
}

export default PGPlusFlow
